import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { StorageService } from '../../core/storage/storage.service';
import { AuthFacade } from '../../stores/auth/facades/auth.facade';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class CredentialsInterceptor implements HttpInterceptor {

  constructor(
    private storage: StorageService,
    private authFacade: AuthFacade,
    private authenticationService: AuthenticationService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const currentUser = this.storage.get('currentUser');

    if (request.url.indexOf('assets/i18n') < 0 && request.url.indexOf('blob.core.windows.net') < 0) {
      request = request.clone({
        withCredentials: true
      });
    }

    if (request.url.indexOf('assets/i18n') < 0 && currentUser && request.url.indexOf('blob.core.windows.net') < 0) {
      request = this.addToken(request, currentUser.accessToken, currentUser.language);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401
          && request.url.indexOf('login') < 0
          && request.url.indexOf('logout') < 0
          && request.url.indexOf('refresh') < 0
          && request.url.indexOf('/Auth/session') < 0
          && window.location.href.indexOf('signin') < 0
        ) {
          return this.handle401Error(request, next);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Sets rather than appends the header. Appending sent two credentials when the request already
   * carried one: the refresh call, which adds its own, went out as `Bearer x, Bearer x` and the
   * API rejected it with 400, and a request retried after a refresh carried both the expired and
   * the new token.
   */
  private addToken(request: HttpRequest<any>, token: string, language?: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers
        .set('authorization', `Bearer ${token}`)
        .set('accept-language', language || 'pl')
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const currentUser = this.storage.get('currentUser');
    if (!currentUser?.accessToken) {
      this._endSession();
      return throwError(() => new Error('Missing current user'));
    }

    return this.authenticationService.refreshOnce(currentUser.accessToken).pipe(
      switchMap(response => {
        if (!response?.data?.accessToken) {
          return throwError(() => new Error('Refresh failed'));
        }
        const newAccessToken = response.data.accessToken;
        const credentials = {
          accessToken: newAccessToken,
          expirationTokenDate: response.data.expirationTokenDate,
          language: currentUser.language,
          ...(currentUser.username ? { username: currentUser.username } : {}),
        };
        this.storage.set('currentUser', JSON.stringify(credentials));
        this.authFacade.updateToken(newAccessToken);
        return next.handle(this.addToken(request, newAccessToken, credentials.language));
      }),
      catchError(error => {
        if (error?.status === 401 || error?.status === 403) {
          this._endSession();
        }
        return throwError(() => error);
      })
    );
  }

  private _endSession(): void {
    this.authFacade.autoLogout();
    this.authFacade.loginRedirect('');
  }
}
